/* eslint-disable no-nested-ternary */
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import { body3 } from 'src/theme/styles/text';
import { useSettingsContext } from 'src/components/settings';
import { useNavigate } from 'react-router';
import Iconify from '../../iconify';
//
import { NavItemProps, NavConfigProps } from '../types';
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

type Props = NavItemProps & {
  config: NavConfigProps;
};

export default function NavItem({
  item,
  open,
  depth,
  active,
  config,
  externalLink,
  disabled,
  ...other
}: Props) {
  const { title, path, icon, info, children, caption, roles } = item;

  const subItem = depth !== 1;

  const settings = useSettingsContext();

  const mini = settings.themeLayout === 'mini';

  const navigate = useNavigate();

  const renderContent = (
    <StyledItem
      disableGutters
      disabled={disabled}
      active={active}
      depth={depth}
      config={config}
      sx={
        mini
          ? { display: 'flex', flexDirection: 'column' }
          : { marginLeft: subItem ? '31px' : '0px', marginBottom: subItem ? '0px' : '8px' }
      }
      {...other}
    >
      <>
        {icon && (
          <StyledIcon size={config.iconSize} mr={mini ? '0px' : '8px'}>
            {icon}
          </StyledIcon>
        )}
      </>

      {!(config.hiddenLabel && !subItem) && (
        <ListItemText
          primary={title}
          sx={{ width: '100%' }}
          secondary={
            caption ? (
              <Tooltip title={caption} placement="top-start">
                <span>{caption}</span>
              </Tooltip>
            ) : null
          }
          primaryTypographyProps={{
            noWrap: true,
            typography: subItem ? 'body1' : mini ? { ...body3 } : 'body1',
            letterSpacing: mini ? '-0.24px' : '-0.32px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: mini && !subItem ? 'center' : 'start',
            marginTop: mini ? '4px' : '0px',
            color: subItem
              ? active
                ? 'body.primary'
                : 'body.quaternary'
              : active
              ? 'white'
              : 'body.primary',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            component: 'span',
            typography: 'caption',
          }}
        />
      )}

      {subItem && active && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path d="M6 4L10 8L6 12" stroke="black" />
        </svg>
      )}

      {info && (
        <Box component="span" sx={{ ml: 1, lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {!!children && !mini && (
        <Iconify
          width={16}
          icon={open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  // Hidden item by role
  if (roles && !roles.includes(`${config.currentRole}`)) {
    return null;
  }

  // External link
  if (externalLink)
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        color="inherit"
        sx={{
          ...(disabled && {
            cursor: 'default',
          }),
        }}
      >
        {renderContent}
      </Link>
    );

  // Has child
  // if (children) {
  //   return renderContent;
  // }

  // Default
  return (
    <Box
      onClick={() => {
        if (!disabled) {
          navigate(path);
        }
      }}
      color="inherit"
      sx={{
        ...(disabled && {
          cursor: 'default',
        }),
      }}
    >
      {renderContent}
    </Box>
  );
}
